import React, { useState, useEffect } from 'react';
import { solicitudesAPI, comprasAPI, bajasAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Eye, Loader2, Wrench, Users, ShoppingCart, FileText, LogOut, Pause, Plane, Phone, Mail, Globe, MapPin, Building2 } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

// Labels de motivos
const MOTIVOS_LABELS = {
  'demasiado_caro': 'Demasiado caro',
  'no_uso': 'No lo uso lo suficiente',
  'problemas_tecnicos': 'Problemas técnicos',
  'mal_servicio': 'Mal servicio',
  'cambio_proveedor': 'Cambio de proveedor',
  'cierre_taller': 'Cierre del taller',
  'pausa_temporal': 'Pausa temporal',
  'otro': 'Otro motivo'
};

const AdminSolicitudes = () => {
  const [solicitudesSuscripcion, setSolicitudesSuscripcion] = useState([]);
  const [solicitudesCompra, setSolicitudesCompra] = useState([]);
  const [solicitudesUsuarioExtra, setSolicitudesUsuarioExtra] = useState([]);
  const [solicitudesCapacidades, setSolicitudesCapacidades] = useState([]);
  const [solicitudesBaja, setSolicitudesBaja] = useState([]);
  const [solicitudesItinerantes, setSolicitudesItinerantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showDetailItinerante, setShowDetailItinerante] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showRejectCapacidad, setShowRejectCapacidad] = useState(false);
  const [showRejectBaja, setShowRejectBaja] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [suscRes, compraRes, extraRes, capacidadesRes, bajasRes, itinerantesRes] = await Promise.all([
        solicitudesAPI.getSuscripcion(),
        comprasAPI.getSolicitudes(),
        solicitudesAPI.getUsuariosExtra(),
        solicitudesAPI.getCapacidades(),
        bajasAPI.getAll(),
        solicitudesAPI.getTalleresItinerantes()
      ]);
      setSolicitudesSuscripcion(suscRes.data);
      setSolicitudesCompra(compraRes.data);
      setSolicitudesUsuarioExtra(extraRes.data || []);
      setSolicitudesCapacidades(capacidadesRes.data || []);
      setSolicitudesBaja(bajasRes.data || []);
      setSolicitudesItinerantes(itinerantesRes.data?.data || []);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleAprobarSuscripcion = async (id) => {
    setProcessing(true);
    try {
      const result = await solicitudesAPI.aprobarSuscripcion(id);
      toast.success(`Solicitud aprobada. Password temporal: ${result.data.password_temporal}`);
      loadData();
      setShowDetail(false);
    } catch (error) {
      toast.error(formatApiError(error, 'Error al aprobar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazarSuscripcion = async () => {
    if (!motivoRechazo) {
      toast.error('El motivo es obligatorio');
      return;
    }
    setProcessing(true);
    try {
      await solicitudesAPI.rechazarSuscripcion(selectedSolicitud.id, motivoRechazo);
      toast.success('Solicitud rechazada');
      setShowReject(false);
      setShowDetail(false);
      setMotivoRechazo('');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al rechazar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleAprobarCompra = async (id) => {
    setProcessing(true);
    try {
      await comprasAPI.aprobarSolicitud(id);
      toast.success('Compra aprobada, permisos otorgados');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al aprobar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleAprobarUsuarioExtra = async (id) => {
    setProcessing(true);
    try {
      await solicitudesAPI.aprobarUsuariosExtra(id);
      toast.success('Solicitud aprobada. Se ha habilitado el espacio para el usuario adicional.');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al aprobar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleAprobarCapacidades = async (id) => {
    setProcessing(true);
    try {
      await solicitudesAPI.aprobarCapacidades(id);
      toast.success('Capacidades temporales aprobadas por 30 días');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al aprobar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazarCapacidades = async () => {
    if (!motivoRechazo) {
      toast.error('El motivo es obligatorio');
      return;
    }
    setProcessing(true);
    try {
      await solicitudesAPI.rechazarCapacidades(selectedSolicitud.id, motivoRechazo);
      toast.success('Solicitud de capacidades rechazada');
      setShowRejectCapacidad(false);
      setMotivoRechazo('');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al rechazar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleAceptarBaja = async (id) => {
    setProcessing(true);
    try {
      const result = await bajasAPI.aceptar(id);
      toast.success(result.data.message || 'Baja aceptada');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al aceptar baja'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRechazarBaja = async () => {
    setProcessing(true);
    try {
      await bajasAPI.rechazar(selectedSolicitud.id, motivoRechazo);
      toast.success('¡Cliente retenido! Solicitud cancelada.');
      setShowRejectBaja(false);
      setMotivoRechazo('');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleCambiarEstadoItinerante = async (id, nuevoEstado) => {
    setProcessing(true);
    try {
      await solicitudesAPI.actualizarEstadoTallerItinerante(id, nuevoEstado);
      toast.success(`Estado actualizado a "${nuevoEstado}"`);
      loadData();
      setShowDetailItinerante(false);
    } catch (error) {
      toast.error(formatApiError(error, 'Error al actualizar estado'));
    } finally {
      setProcessing(false);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'aprobada': return <Badge className="bg-green-500">Aprobada</Badge>;
      case 'completada': return <Badge className="bg-green-600">Completada</Badge>;
      case 'pagada': return <Badge className="bg-blue-500">Pagada</Badge>;
      case 'rechazada': return <Badge variant="destructive">Rechazada</Badge>;
      case 'aprobada_pendiente_pago': return <Badge className="bg-amber-500">Pendiente de Pago</Badge>;
      default: return <Badge variant="outline" className="border-amber-500 text-amber-600">Pendiente</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendientesSusc = solicitudesSuscripcion.filter(s => s.estado === 'pendiente');
  const pendientesCompra = solicitudesCompra.filter(s => s.estado === 'pendiente');
  const pendientesExtra = solicitudesUsuarioExtra.filter(s => s.estado === 'pendiente');
  const pendientesCapacidades = solicitudesCapacidades.filter(s => s.estado === 'pendiente');
  const pendientesBaja = solicitudesBaja.filter(s => s.status === 'pendiente' || s.status === 'pausado');
  const pendientesItinerantes = solicitudesItinerantes.filter(s => s.estado === 'pendiente');

  const getEstadoItineranteBadge = (estado) => {
    switch (estado) {
      case 'contactado': return <Badge className="bg-blue-500">Contactado</Badge>;
      case 'en_negociacion': return <Badge className="bg-purple-500">En Negociación</Badge>;
      case 'aprobado': return <Badge className="bg-green-500">Aprobado</Badge>;
      case 'rechazado': return <Badge variant="destructive">Rechazado</Badge>;
      default: return <Badge variant="outline" className="border-amber-500 text-amber-600">Pendiente</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold mb-6">Gestión de Solicitudes y Compras</h1>

        <Tabs defaultValue="suscripcion">
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            <TabsTrigger value="suscripcion" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Suscripciones
              {pendientesSusc.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesSusc.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="compras" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Documentación
              {pendientesCompra.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesCompra.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="usuarios-extra" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuarios Extra
              {pendientesExtra.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesExtra.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="capacidades" className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Capacidades Temp.
              {pendientesCapacidades.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesCapacidades.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="bajas" className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Gestión Bajas
              {pendientesBaja.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesBaja.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="itinerantes" className="flex items-center gap-2">
              <Plane className="h-4 w-4" />
              Talleres Itinerantes
              {pendientesItinerantes.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendientesItinerantes.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TAB: Suscripciones */}
          <TabsContent value="suscripcion">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <p className="text-center py-8">Cargando...</p>
                ) : solicitudesSuscripcion.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay solicitudes</p>
                ) : (
                  <div className="space-y-4">
                    {solicitudesSuscripcion.map(sol => (
                      <div key={sol.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium">{sol.razon_social}</p>
                          <p className="text-sm text-gray-500">ANAC: {sol.numero_anac} | {sol.pais}, {sol.provincia}</p>
                          <p className="text-sm text-gray-500">Nivel: <span className="font-medium capitalize">{sol.nivel_solicitado}</span></p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getEstadoBadge(sol.estado)}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedSolicitud(sol); setShowDetail(true); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {sol.estado === 'pendiente' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => handleAprobarSuscripcion(sol.id)}
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => { setSelectedSolicitud(sol); setShowReject(true); }}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Documentación (compras de documentos) */}
          <TabsContent value="compras">
            <Card>
              <CardContent className="pt-6">
                {solicitudesCompra.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay compras de documentación</p>
                ) : (
                  <div className="space-y-4">
                    {solicitudesCompra.map(sol => (
                      <div key={sol.id} className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${sol.estado === 'completada' ? 'bg-green-50 border-green-200' : ''}`}>
                        <div className="flex-1">
                          <p className="font-medium">{sol.taller_nombre}</p>
                          <p className="text-sm text-gray-500">ANAC: {sol.taller_numero_anac}</p>
                          {/* Mostrar nombres de documentos si existen */}
                          {sol.documentos_nombres && sol.documentos_nombres.length > 0 ? (
                            <div className="mt-1">
                              <p className="text-sm font-medium text-gray-700">Documentos:</p>
                              <ul className="text-sm text-gray-600 list-disc list-inside">
                                {sol.documentos_nombres.slice(0, 3).map((nombre, idx) => (
                                  <li key={idx} className="truncate max-w-md">{nombre}</li>
                                ))}
                                {sol.documentos_nombres.length > 3 && (
                                  <li className="text-gray-400">y {sol.documentos_nombres.length - 3} más...</li>
                                )}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">{sol.items?.length || 0} documento(s)</p>
                          )}
                          {/* Mostrar monto en ARS si existe, sino USD */}
                          <p className="text-sm text-green-600 font-medium mt-1">
                            {sol.monto_ars ? (
                              <>Monto: ${sol.monto_ars.toLocaleString('es-AR', {maximumFractionDigits: 0})} ARS</>
                            ) : (
                              <>Monto: ${sol.total_usd?.toFixed(2)} USD</>
                            )}
                            {sol.automatico && <Badge variant="outline" className="ml-2 text-xs">Automático</Badge>}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(sol.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getEstadoBadge(sol.estado)}
                          {sol.estado === 'pendiente' && (
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => handleAprobarCompra(sol.id)}
                              disabled={processing}
                            >
                              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              Aprobar
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Usuarios Extra */}
          <TabsContent value="usuarios-extra">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Solicitudes de Usuarios Adicionales</CardTitle>
                <p className="text-sm text-gray-500">Talleres que solicitan usuarios extra ($50/mes o $450/año por usuario)</p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8">Cargando...</p>
                ) : solicitudesUsuarioExtra.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay solicitudes de usuarios extra</p>
                ) : (
                  <div className="space-y-4">
                    {solicitudesUsuarioExtra.map(sol => (
                      <div key={sol.id} className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${sol.estado === 'completada' ? 'bg-green-50 border-green-200' : ''}`}>
                        <div className="flex-1">
                          <p className="font-medium">{sol.taller_nombre || 'Taller'}</p>
                          <p className="text-sm text-gray-500">
                            Cantidad: <span className="font-medium">{sol.cantidad || 1} usuario(s)</span>
                            {sol.periodo && <> • Plan: <span className="font-medium">{sol.periodo}</span></>}
                          </p>
                          <p className="text-sm text-green-600 font-medium">
                            {sol.monto_ars ? (
                              <>Monto: ${sol.monto_ars.toLocaleString('es-AR', {maximumFractionDigits: 0})} ARS</>
                            ) : (
                              <>Costo: ${sol.total_usd?.toFixed(2) || ((sol.cantidad || 1) * 50)} USD</>
                            )}
                            {sol.automatico && <Badge variant="outline" className="ml-2 text-xs">Automático</Badge>}
                          </p>
                          {sol.notas && (
                            <p className="text-sm text-gray-400 mt-1">Notas: {sol.notas}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Fecha: {formatDate(sol.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getEstadoBadge(sol.estado)}
                          {sol.estado === 'pendiente' && (
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => handleAprobarUsuarioExtra(sol.id)}
                              disabled={processing}
                            >
                              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                              Aprobar
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Capacidades Temporales */}
          <TabsContent value="capacidades">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-teal-600" />
                  Solicitudes de Capacidades Temporales
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Talleres que solicitan acceso temporal (30 días) a modelos que no están en su certificación ANAC
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8">Cargando...</p>
                ) : solicitudesCapacidades.length === 0 ? (
                  <div className="text-center py-8">
                    <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay solicitudes de capacidades temporales</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {solicitudesCapacidades.map(sol => (
                      <div key={sol.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-medium text-lg">{sol.taller_nombre || 'Taller'}</p>
                              {getEstadoBadge(sol.estado)}
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">Fabricante:</p>
                                <p className="font-medium">{sol.fabricante_nombre || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Modelos solicitados:</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(sol.modelos_nombres || []).map((modelo, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {modelo}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {sol.notas && (
                              <p className="text-sm text-gray-500 mt-2 italic">"{sol.notas}"</p>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-2">
                              Solicitado: {formatDate(sol.created_at)}
                              {sol.solicitante_nombre && ` por ${sol.solicitante_nombre}`}
                            </p>
                          </div>
                          
                          {sol.estado === 'pendiente' && (
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => handleAprobarCapacidades(sol.id)}
                                disabled={processing}
                              >
                                {processing ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Aprobar 30 días
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => { 
                                  setSelectedSolicitud(sol); 
                                  setShowRejectCapacidad(true); 
                                }}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Rechazar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Gestión de Bajas */}
          <TabsContent value="bajas">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <LogOut className="h-5 w-5 text-red-600" />
                  Gestión de Bajas y Cancelaciones
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Solicitudes de baja de suscripción y pausas temporales
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8">Cargando...</p>
                ) : solicitudesBaja.length === 0 ? (
                  <div className="text-center py-8">
                    <LogOut className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay solicitudes de baja</p>
                    <p className="text-xs text-gray-400 mt-1">¡Buen trabajo reteniendo clientes! 🎉</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {solicitudesBaja.map(sol => (
                      <div key={sol.id} className={`p-4 border rounded-lg ${
                        sol.status === 'pausado' ? 'bg-indigo-50 border-indigo-200' :
                        sol.status === 'pendiente' ? 'bg-red-50 border-red-200' :
                        sol.status === 'aceptada' ? 'bg-amber-50 border-amber-200' :
                        'bg-green-50 border-green-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-medium text-lg">{sol.taller_nombre}</p>
                              <Badge className={
                                sol.status === 'pausado' ? 'bg-indigo-500' :
                                sol.status === 'pendiente' ? 'bg-red-500' :
                                sol.status === 'aceptada' ? 'bg-amber-500' :
                                'bg-green-500'
                              }>
                                {sol.status === 'pausado' && '⏸️ Pausado'}
                                {sol.status === 'pendiente' && '⏳ Pendiente'}
                                {sol.status === 'aceptada' && '✓ Aceptada'}
                                {sol.status === 'rechazada' && '🎉 Retenido'}
                              </Badge>
                              {sol.es_pausa && (
                                <Badge variant="outline" className="border-indigo-300 text-indigo-600">
                                  <Pause className="h-3 w-3 mr-1" />
                                  Pausa temporal
                                </Badge>
                              )}
                              <Badge variant="outline">{sol.nivel_suscripcion?.toUpperCase()}</Badge>
                            </div>
                            
                            <p className="text-sm text-gray-500 mb-2">
                              ANAC: {sol.taller_numero_anac} | Solicitado por: {sol.solicitante_nombre}
                            </p>
                            
                            {/* Motivos */}
                            <div className="mb-2">
                              <p className="text-sm font-medium text-gray-700">Motivos:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(sol.motivos || []).map((m, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs bg-gray-200">
                                    {MOTIVOS_LABELS[m] || m}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            {sol.detalle && (
                              <p className="text-sm text-gray-600 italic bg-white/50 p-2 rounded">
                                "{sol.detalle}"
                              </p>
                            )}
                            
                            {sol.pausa_hasta && (
                              <p className="text-sm text-indigo-600 mt-2">
                                <strong>Pausado hasta:</strong> {sol.pausa_hasta?.split('T')[0]}
                              </p>
                            )}
                            
                            {sol.fecha_fin_acceso && (
                              <p className="text-sm text-amber-600 mt-2">
                                <strong>Acceso hasta:</strong> {sol.fecha_fin_acceso?.split('T')[0]}
                              </p>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-2">
                              Solicitado: {formatDate(sol.created_at)}
                            </p>
                          </div>
                          
                          {(sol.status === 'pendiente' || sol.status === 'pausado') && (
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => handleAceptarBaja(sol.id)}
                                disabled={processing}
                              >
                                {processing ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Aceptar Baja
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => { 
                                  setSelectedSolicitud(sol); 
                                  setShowRejectBaja(true); 
                                }}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Retener Cliente
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Talleres Itinerantes */}
          <TabsContent value="itinerantes">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Plane className="h-5 w-5 text-sky-600" />
                  Solicitudes de Talleres Itinerantes
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Talleres sin certificación específica que solicitan planes personalizados
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8">Cargando...</p>
                ) : solicitudesItinerantes.length === 0 ? (
                  <div className="text-center py-8">
                    <Plane className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay solicitudes de talleres itinerantes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {solicitudesItinerantes.map(sol => (
                      <div key={sol.id} className={`p-4 border rounded-lg hover:bg-gray-50 ${
                        sol.estado === 'pendiente' ? 'border-l-4 border-l-amber-400' : ''
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="h-5 w-5 text-sky-600" />
                              <p className="font-medium text-lg">{sol.razon_social}</p>
                              {getEstadoItineranteBadge(sol.estado)}
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="h-4 w-4" />
                                <a href={`mailto:${sol.email}`} className="hover:text-sky-600">{sol.email}</a>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="h-4 w-4" />
                                <a href={`tel:${sol.telefono}`} className="hover:text-sky-600">{sol.telefono}</a>
                              </div>
                              {sol.pais && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Globe className="h-4 w-4" />
                                  {sol.pais}{sol.provincia ? `, ${sol.provincia}` : ''}
                                </div>
                              )}
                              {sol.aerodromo && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <MapPin className="h-4 w-4" />
                                  {sol.aerodromo}
                                </div>
                              )}
                            </div>

                            <p className="text-sm text-gray-500">
                              Contacto: <span className="font-medium">{sol.nombre_completo}</span> 
                              {sol.documento_identidad && ` (Doc: ${sol.documento_identidad})`}
                            </p>
                            
                            {sol.comentarios && (
                              <p className="text-sm text-gray-500 mt-2 italic bg-gray-50 p-2 rounded">
                                &ldquo;{sol.comentarios}&rdquo;
                              </p>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-2">
                              Solicitado: {formatDate(sol.created_at)}
                              {sol.contacto_email && ' • Prefiere email'}
                              {sol.contacto_telefono && ' • Prefiere llamada'}
                            </p>
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedSolicitud(sol); setShowDetailItinerante(true); }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            {sol.estado === 'pendiente' && (
                              <Button
                                size="sm"
                                className="bg-blue-500 hover:bg-blue-600"
                                onClick={() => handleCambiarEstadoItinerante(sol.id, 'contactado')}
                                disabled={processing}
                              >
                                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4 mr-1" />}
                                Contactado
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Detail Dialog for Suscripcion */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud de Suscripción</DialogTitle>
            </DialogHeader>
            {selectedSolicitud && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Razón Social</Label>
                    <p className="font-medium">{selectedSolicitud.razon_social}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Número ANAC</Label>
                    <p className="font-medium">{selectedSolicitud.numero_anac}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">País</Label>
                    <p>{selectedSolicitud.pais}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Provincia</Label>
                    <p>{selectedSolicitud.provincia}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Nivel Solicitado</Label>
                    <p className="capitalize font-medium">{selectedSolicitud.nivel_solicitado}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Estado</Label>
                    <p>{getEstadoBadge(selectedSolicitud.estado)}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Contacto</Label>
                  <p>{selectedSolicitud.nombre_contacto}</p>
                  <p className="text-sm text-gray-500">{selectedSolicitud.email_contacto}</p>
                  <p className="text-sm text-gray-500">{selectedSolicitud.telefono_contacto}</p>
                </div>
                {selectedSolicitud.notas && (
                  <div>
                    <Label className="text-gray-500">Notas</Label>
                    <p className="text-sm">{selectedSolicitud.notas}</p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Fecha de Solicitud</Label>
                  <p className="text-sm">{formatDate(selectedSolicitud.created_at)}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog for Suscripcion */}
        <Dialog open={showReject} onOpenChange={setShowReject}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rechazar Solicitud de Suscripción</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Motivo del rechazo *</Label>
                <Textarea
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Explique el motivo del rechazo..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReject(false)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleRechazarSuscripcion}
                disabled={processing}
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Rechazar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog for Capacidades */}
        <Dialog open={showRejectCapacidad} onOpenChange={setShowRejectCapacidad}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rechazar Solicitud de Capacidades</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                El taller será notificado del rechazo y podrá contactarse con soporte para más información.
              </p>
              <div>
                <Label>Motivo del rechazo *</Label>
                <Textarea
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Explique el motivo del rechazo..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRejectCapacidad(false); setMotivoRechazo(''); }}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleRechazarCapacidades}
                disabled={processing || !motivoRechazo}
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Rechazar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog for Baja (Retener cliente) */}
        <Dialog open={showRejectBaja} onOpenChange={setShowRejectBaja}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                🎉 Retener Cliente
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Al rechazar esta solicitud, el cliente será notificado de que su baja fue cancelada y continuará con su suscripción activa.
              </p>
              <div>
                <Label>Nota interna (opcional)</Label>
                <Textarea
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="¿Qué acordaste con el cliente? ¿Ofreciste algún descuento?"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRejectBaja(false); setMotivoRechazo(''); }}>
                Cancelar
              </Button>
              <Button 
                className="bg-green-500 hover:bg-green-600"
                onClick={handleRechazarBaja}
                disabled={processing}
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirmar Retención
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog for Taller Itinerante */}
        <Dialog open={showDetailItinerante} onOpenChange={setShowDetailItinerante}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plane className="h-5 w-5 text-sky-600" />
                Solicitud de Taller Itinerante
              </DialogTitle>
            </DialogHeader>
            {selectedSolicitud && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">{selectedSolicitud.razon_social}</h3>
                  {getEstadoItineranteBadge(selectedSolicitud.estado)}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Datos del Contacto</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-gray-500">Nombre</Label>
                      <p className="font-medium">{selectedSolicitud.nombre_completo}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Documento</Label>
                      <p className="font-medium">{selectedSolicitud.documento_identidad || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Email</Label>
                      <a href={`mailto:${selectedSolicitud.email}`} className="font-medium text-sky-600 hover:underline">
                        {selectedSolicitud.email}
                      </a>
                    </div>
                    <div>
                      <Label className="text-gray-500">Teléfono</Label>
                      <a href={`tel:${selectedSolicitud.telefono}`} className="font-medium text-sky-600 hover:underline">
                        {selectedSolicitud.telefono}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-sky-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-sm text-sky-700 border-b border-sky-200 pb-2">Datos del Taller</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-gray-500">País</Label>
                      <p className="font-medium">{selectedSolicitud.pais || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Provincia</Label>
                      <p className="font-medium">{selectedSolicitud.provincia || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Dirección</Label>
                      <p className="font-medium">{selectedSolicitud.direccion || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Aeródromo</Label>
                      <p className="font-medium">{selectedSolicitud.aerodromo || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Nº Certificación</Label>
                      <p className="font-medium">{selectedSolicitud.numero_anac || 'No aplica'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Web</Label>
                      <p className="font-medium">{selectedSolicitud.pagina_web || '-'}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-500">Preferencia de contacto</Label>
                  <p className="text-sm">
                    {selectedSolicitud.contacto_email && '✉️ Email '}
                    {selectedSolicitud.contacto_telefono && '📞 Llamada'}
                    {!selectedSolicitud.contacto_email && !selectedSolicitud.contacto_telefono && 'No especificado'}
                  </p>
                </div>

                {selectedSolicitud.comentarios && (
                  <div>
                    <Label className="text-gray-500">Comentarios</Label>
                    <p className="text-sm bg-gray-50 p-3 rounded italic">&ldquo;{selectedSolicitud.comentarios}&rdquo;</p>
                  </div>
                )}

                <div>
                  <Label className="text-gray-500">Fecha de solicitud</Label>
                  <p className="text-sm">{formatDate(selectedSolicitud.created_at)}</p>
                </div>

                {/* Acciones de estado */}
                <div className="border-t pt-4">
                  <Label className="text-gray-500 mb-2 block">Cambiar estado</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSolicitud.estado !== 'contactado' && (
                      <Button
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600"
                        onClick={() => handleCambiarEstadoItinerante(selectedSolicitud.id, 'contactado')}
                        disabled={processing}
                      >
                        <Phone className="h-4 w-4 mr-1" /> Contactado
                      </Button>
                    )}
                    {selectedSolicitud.estado !== 'en_negociacion' && (
                      <Button
                        size="sm"
                        className="bg-purple-500 hover:bg-purple-600"
                        onClick={() => handleCambiarEstadoItinerante(selectedSolicitud.id, 'en_negociacion')}
                        disabled={processing}
                      >
                        En Negociación
                      </Button>
                    )}
                    {selectedSolicitud.estado !== 'aprobado' && (
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleCambiarEstadoItinerante(selectedSolicitud.id, 'aprobado')}
                        disabled={processing}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" /> Aprobado
                      </Button>
                    )}
                    {selectedSolicitud.estado !== 'rechazado' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCambiarEstadoItinerante(selectedSolicitud.id, 'rechazado')}
                        disabled={processing}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Rechazar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminSolicitudes;
